// App menu switches (spec §5 / plan R1–R4). Every future build of this app uses
// the saved menu; switched-off pages are "Open on the web" in the app.
import React, { useEffect, useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import type { MobileAppId, ReleaseEnv } from '@platform/shared-types';
import { RubyLoader } from '../../components/RubyLoader';
import { api } from '../../services/apiService';
import { navGroups } from '../../components/Layout';
import { mobileApi } from './mobileApi';
import { rubyTillEntries, adminEntries, countOn, toEntries, lockTableReady } from './appMenuEntries';
import type { AppMenuRow } from './appMenuEntries';
import { Card, Chip, Switch, Btn, PLATFORMS } from './ui';

export function AppMenuEditor({ app, env, notify }: { app: MobileAppId; env: ReleaseEnv; notify: (t: 'success' | 'error', m: string) => void }) {
  const [rows, setRows] = useState<AppMenuRow[] | null>(null);
  const [saved, setSaved] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let off = false;
    setRows(null); setErr('');
    (async () => {
      try {
        const menu = (await mobileApi.appMenu(app, env)).menu;
        const r = app === 'rubytill'
          ? rubyTillEntries((await api.sidebar.get('agenticpos')).sidebarConfig, menu)
          : adminEntries(navGroups as any, menu);
        if (!off) { setRows(r); setSaved(JSON.stringify(toEntries(r))); }
      } catch (e: any) { if (!off) setErr(e.message || 'Could not load the menu'); }
    })();
    return () => { off = true; };
  }, [app, env]);

  const groups = useMemo(() => {
    const m = new Map<string, AppMenuRow[]>();
    for (const r of rows ?? []) m.set(r.group, [...(m.get(r.group) ?? []), r]);
    return [...m.entries()];
  }, [rows]);
  const dirty = !!rows && JSON.stringify(toEntries(rows)) !== saved;
  const set = (id: string, p: 'ios' | 'android', v: boolean) => setRows((rs) => rs!.map((r) => (r.id === id ? { ...r, [p]: v } : r)));
  const appName = app === 'rubytill' ? 'RubyTill' : 'RubyTill Admin';

  return (
    <Card title={`${appName} app menu`} sub="On / off only. Used by every deploy."
      right={rows && <div className="flex flex-wrap items-center gap-2">
        {PLATFORMS.map((p) => <Chip key={p.id} tone="ruby">{p.label} {countOn(rows, p.id)}/{rows.length}</Chip>)}
        <Btn primary disabled={!dirty || saving || !lockTableReady} onClick={async () => {
          setSaving(true);
          try { await mobileApi.saveAppMenu(app, env, toEntries(rows)); setSaved(JSON.stringify(toEntries(rows))); notify('success', 'App menu saved — every future deploy uses it'); }
          catch (e: any) { notify('error', e.message); } finally { setSaving(false); }
        }}>Save app menu</Btn>
      </div>}>
      {!lockTableReady && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Purchase-lock list didn't load. Restart the dev server, then reload.</p>}
      {err && <p className="text-sm text-red-700">{err}</p>}
      {!rows && !err && <div className="relative h-40"><RubyLoader variant="section" /></div>}
      {rows && (
        <div className="flex flex-col gap-3">
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900"><b>Off = not in the app.</b> Web only, with an "Open on the web" link. Billing is always web only.</p>
          {groups.map(([group, items]) => (
            <div key={group} className="overflow-hidden rounded-xl border border-stone-200">
              <div className="grid grid-cols-[1fr_72px_72px] items-center gap-2 bg-stone-50 px-4 py-2 text-sm font-semibold">
                <span>{group}</span><span className="text-center text-xs uppercase tracking-wide text-stone-500">iPhone</span><span className="text-center text-xs uppercase tracking-wide text-stone-500">Android</span>
              </div>
              {items.map((r) => (
                <div key={r.id} className="grid min-h-[48px] grid-cols-[1fr_72px_72px] items-center gap-2 border-t border-stone-100 px-4">
                  <span className="flex flex-wrap items-center gap-2 text-sm">{r.label}<span className="font-mono text-[11px] text-stone-400">{r.route}</span></span>
                  {r.locked
                    ? <span className="col-span-2 flex items-center justify-center gap-1 text-xs text-stone-500"><Lock size={13} />Web only</span>
                    : PLATFORMS.map((p) => <div key={p.id} className="flex justify-center"><Switch on={r[p.id]} onChange={(v) => set(r.id, p.id, v)} label={`${r.label} on ${p.label}`} /></div>)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
